/**
 * The Analytics dashboard grid — every number that decides where a card can sit.
 *
 * Kept out of the components so the seeds, the canvas, the Customize panel and the
 * view hook all measure with the same ruler. A constant that lived in the canvas
 * would have to be duplicated in the seeds, and the day the two disagreed a seeded
 * view would place a card somewhere the grid refuses to keep it.
 *
 * ## 12 columns, not 7
 *
 * 7 was the first proposal and it is the one number that cannot work here: it is
 * prime, so it has no halves, no thirds and no quarters, and the four KPI tiles that
 * open every seeded view would take 4 of 7 columns and leave 3 dangling with nothing
 * that fits them. 12 divides by 2, 3, 4 and 6, which is why every dashboard grid
 * worth copying (Grafana's 24, Datadog's 12) picked a highly-composite number.
 *
 * ## The row unit IS the KPI tile
 *
 * `ROW_HEIGHT` is `CARD_MIN_HEIGHT` (160) — the height `AnalyticsBlock` already
 * renders at on this page, on Tags -> Inventory and on Billing. So `h: 1` means
 * exactly one stat tile, and every taller card is expressed as a whole number of
 * them. Picking any other unit would make the one fixed-height card on the page a
 * fraction, and the whole grid would inherit that fraction.
 *
 * ## KPI tiles are 3 x 1 and NOT resizable
 *
 * A stat tile shows one number. There is nothing inside it that a larger box would
 * reveal, so a resize handle on one offers a change with no content behind it — the
 * card would simply grow whitespace. Fixing it at 3 x 1 also makes the tile row's
 * arithmetic true at every breakpoint: 3 divides 12 (four across) and 6 (two
 * across), so a strip of tiles never leaves a ragged remainder.
 *
 * A 2-column tile (six across) was built and measured on request; it clips the tile's
 * own content, and `ANALYTICS_CARD_SIZE_DEFAULTS` records the numbers.
 *
 * Tiles remain DRAGGABLE. Position is a preference; size is not.
 */
import type { AnalyticsGridBreakpoint, AnalyticsGridItem } from 'types'

import type { AnalyticsCardKind } from './AnalyticsCardShell'
/*
  ⚠️ TYPE-ONLY, and it is the one import from `analyticsDisplay` this file is allowed.

  The header on `VARIANT_SEPARATOR` below says this module "imports nothing from either"
  (`analyticsDisplay` / `analyticsViewSeeds`) and that is what makes it the safe home for
  the id builder — a runtime edge back would close a cycle whose failure mode is a TDZ
  error at module init, i.e. a blank page rather than a compile error.

  `import type` is ERASED by the compiler, so it creates no runtime edge and no cycle. The
  grouping union is declared next door because that is where the capability record that
  answers for it lives; duplicating the two string literals here so the id module could
  stay literally import-free would be the second expression of one rule that this whole
  file's header warns about.
*/
import type { AnalyticsMapGrouping } from './analyticsDisplay'
// Type-only, and one-directional: `analyticsSolutions` imports nothing from here.
import type { AnalyticsMetricId } from './analyticsSolutions'

/**
 * Minimum width for each breakpoint.
 *
 * ⚠️ These are CONTAINER widths, not viewport widths, and the difference is not
 * academic. react-grid-layout compares against the width it is given, which here is
 * the canvas column — and at a 1280px viewport that column MEASURES 928px, because
 * the sidebar takes 304 and the page gutter 48. A threshold written in viewport terms
 * (1024 was the first attempt) therefore puts a 1280px desktop into the NARROW
 * breakpoint: measured, it rendered two 452px stat tiles per row instead of four.
 *
 * 900 is derived from the tile, not chosen: a 3-column tile at 12 columns is
 * `(W - 11*24) / 12 * 3 + 2*24` wide, which is 214px at a 928px container and 157px at
 * a 700px one. The old fixed strip used 220px as its tile minimum, so 12 columns holds
 * at a 1280 desktop and stops being legible somewhere below it. Below 900 the grid
 * halves to 6 columns and the same tile becomes two-across at ~348px.
 *
 * ⚠️ This derivation is a function of the tile's column span, so narrowing the tile
 * reopens it — see `ANALYTICS_CARD_SIZE_DEFAULTS`, which records why the tile stayed
 * at 3 when a 2-column version was measured.
 */
export const ANALYTICS_GRID_BREAKPOINTS: Record<AnalyticsGridBreakpoint, number> = {
  lg: 900,
  sm: 0,
}

/**
 * Columns per breakpoint. BOTH are multiples of 3 — see `AnalyticsGridBreakpoint`.
 *
 * Halving rather than stepping (12 -> 6, not 12 -> 8) keeps a card's width a stable
 * FRACTION across the change: a 6-column panel is half the row at `lg` and the whole
 * row at `sm`, which is the behaviour a reader expects from a narrowing window. At 8
 * columns that same panel would become three quarters of a row, an arrangement
 * nobody chose.
 */
export const ANALYTICS_GRID_COLS: Record<AnalyticsGridBreakpoint, number> = {
  lg: 12,
  sm: 6,
}

/** One grid row = one stat tile. See the header. */
export const ANALYTICS_ROW_HEIGHT = 160

/**
 * Gap between cards, horizontal and vertical.
 *
 * 24px, the page gutter, so the space between two cards reads the same as the space
 * between a card and the edge of the page. The predecessor used two gaps — 12px inside
 * the tile strip, 24px between panels — which a single grid cannot express and should
 * not: one canvas, one rhythm.
 *
 * The gutter it matches is `Layout`'s scroll-container `p: 6`. It used to be stated as
 * "the page's own `p={6}`", which was true but named the wrong 24: `AnalyticsPage` carried
 * a SECOND `p={6}` on top of `Layout`'s, so the page's real gutter was 48 and this margin
 * had silently stopped matching it. Dropping that padding brought the gutter back to 24
 * and made the sentence true again — the value never needed to change, only the reference.
 */
export const ANALYTICS_GRID_MARGIN: [number, number] = [24, 24]

/**
 * The card's DRAG SURFACE — the element that carries `draggable` and starts the gesture.
 *
 * ONE selector for the whole grid, applied at two different levels — see
 * `AnalyticsCardShell` for why a panel puts it on its header row and a stat tile
 * puts it on its whole body.
 *
 * ## ⚠️ It is the NATIVE protocol now, not react-grid-layout's pointer drag
 *
 * This class used to be `dragConfig.handle`: react-grid-layout watched for it and moved
 * the card by pointer, while a separate native drag on the grip merged the card into a
 * chart. Two protocols, two outcomes, and the user had to choose between them BEFORE
 * knowing which one they wanted — because which outcome a drag produces is decided by
 * where it is RELEASED, not by where it was picked up.
 *
 * There is now one gesture. The whole canvas runs on HTML5 drag-and-drop
 * (`dragConfig.enabled` is false — see `AnalyticsCanvas.DRAG_CONFIG`), and the drop
 * TARGET decides: empty grid moves the card, a chart or map merges it. The grip is
 * inside this element rather than beside it, so grabbing the grip and grabbing the card
 * are structurally the same act.
 *
 * ⚠️ THE TWO PROTOCOLS STILL CANNOT SHARE AN ELEMENT — that finding stands and is why
 * react-grid-layout's dragging is switched OFF rather than left on beside this. Putting
 * `draggable` on an element react-grid-layout also treats as a handle engages both: its
 * threshold is 3px and Chrome's native one is ~5px, so react-draggable always wins the
 * start, and the native drag then suppresses `mouseup` for its whole duration, so
 * react-draggable never sees a terminator and never runs `onDragStop`. The card is left
 * stranded — `.react-draggable-dragging` still on it, a transform still applied, nothing
 * committed. No ordering fixes it, because each protocol wins a different END of the
 * gesture. Unification therefore had to mean picking one, not layering them.
 *
 * ## The cost, stated plainly: reposition no longer works by TOUCH
 *
 * HTML5 drag-and-drop has no touch implementation, so on a tablet a card can no longer
 * be moved. Merging never worked there either (it has always been native), so the loss
 * is reposition alone, on a surface that is desktop-only by convention
 * (working-conventions §37 verifies `Platform.*` at 1280x800) and is a data-dense admin
 * canvas with a 12-column grid and drag-to-resize handles. Restoring it means a pointer
 * fallback — a touch shim that synthesises drag events, or `dragConfig` re-enabled ONLY
 * under a coarse-pointer media query — not re-splitting the two gestures, which is the
 * thing this change exists to end.
 */
export const ANALYTICS_DRAG_HANDLE_CLASS = 'analytics-drag-handle'

/**
 * The GRIP — the visible affordance inside a card's control strip.
 *
 * ⚠️ It is no longer a drag source of its own. It is a DESCENDANT of the element carrying
 * `ANALYTICS_DRAG_HANDLE_CLASS`, so a drag begun on it is the card's own drag, byte for
 * byte — same handler, same drag image, same outcomes. That is the entire point: the grip
 * exists for DISCOVERABILITY (nothing else on a card says it can be picked up), not to
 * select between behaviours. It used to carry `draggable` itself, beside a pointer-dragged
 * card surface, which made the two feel like different gestures because they were.
 *
 * The class is kept because it still has two jobs: the `grab` cursor and hover treatment,
 * and being recognisable to the drag-start guard as something that MAY start a drag even
 * though it sits among controls that may not.
 */
export const ANALYTICS_GRIP_CLASS = 'analytics-grip'

/**
 * Descendants a drag must NOT start from.
 *
 * Without this, a card header is unusable: the header carries the card's own control
 * (the map's grouping toggle, the chart's legend) and the expand/remove buttons, and a
 * press on any of them would be swallowed as the beginning of a drag. Matching every
 * `MuiButtonBase-root` covers buttons, icon buttons, tabs and menu items in one
 * clause; the explicit tags cover links and any field a card grows later.
 *
 * ⚠️ THE GRIP IS NO LONGER IN THIS LIST, and its removal is the change. The clause existed
 * to keep react-grid-layout's POINTER drag off an element that carried a native `draggable`
 * — one protocol fenced out of the other's territory. With a single protocol there is
 * nothing to fence: the grip is now simply the part of the drag surface that is visible,
 * and excluding it would make the one control that advertises the gesture the one control
 * that cannot start it.
 *
 * Consumed by `AnalyticsCardShell`'s `dragstart` guard rather than by `dragConfig.cancel`,
 * because react-grid-layout no longer does the dragging. The guard tests `event.target`,
 * so it fences the same descendants the `cancel` option used to.
 */
export const ANALYTICS_DRAG_CANCEL_SELECTOR =
  '.MuiButtonBase-root, button, a, input, select, textarea, [role="button"], [role="tab"], [role="slider"]'

/**
 * The `i` react-grid-layout is told to use for the item currently being dragged IN.
 *
 * ⚠️ A synthetic, reserved id — deliberately NOT the dragged card's own id, and this is
 * a correctness requirement rather than tidiness. react-grid-layout keys its entire drop
 * machinery on `droppingItem.i`:
 *
 *   - `handleDragOver` does `layout.filter(l => l.i !== droppingItem.i)` before appending
 *     the placeholder, so an id that collides with a card ALREADY on the canvas silently
 *     deletes that card's layout entry for the duration of the drag;
 *   - `removeDroppingPlaceholder` filters and compacts on the same key, so a cancelled
 *     drag removes the real card from the grid's internal layout — and nothing restores
 *     it, because the `layout`/`children` props never changed and the library's re-sync
 *     effect is gated on exactly that;
 *   - `handleDrop` resolves the dropped item with `layout.find(l => l.i === droppingItem.i)`,
 *     which would then be ambiguous between the placeholder and the real card.
 *
 * Two live drags collide that way. A card dragged by its own grip obviously does. Less
 * obviously, so does a LIBRARY row: VERA-260 made every capable metric row draggable
 * whether or not it is already placed, so dragging the `certificates_count` row while the
 * `certificates_count` tile sits on the canvas is an ordinary thing to do — and it hit
 * exactly the deletion above.
 *
 * One reserved id for BOTH drag origins removes the whole class of collision, and the
 * mapping back to the real id is a single lookup at drop time (`handleDrop`), where the
 * canvas already knows which drag is running.
 */
export const ANALYTICS_DRAG_ITEM_ID = '__analytics_drag_item__'

/**
 * The card TYPE taxonomy — ONE registry, read by the BADGE on a library row and by the
 * type filter above it.
 *
 * They were two lists a moment ago: the filter derived its options by walking `cards`
 * and collecting whatever string each happened to carry in `category`, and any badge
 * would have needed a second mapping from that string to a label. Two lists that must
 * agree and nothing making them (§44's drift, in miniature) — and the failure would be
 * silent in the worst direction, since a card whose category the filter did not know
 * about simply becomes unreachable whenever the filter is set to anything.
 *
 * Labels are SHORT (§25) — "KPI", not "Key performance indicators" — because they are
 * rendered as a badge on a 340px row beside a title and a control, and as options in a
 * select of the same width. `order` is the filter's option order, authored rather than
 * derived so it does not shuffle when a card is added.
 *
 * ## The categories describe FORM — how a card is DRAWN — and nothing else
 *
 * The first version of this set was `KPI · ROI · Chart · Map`, and it was incoherent: it
 * mixed two independent axes. KPI and ROI say what a number MEANS; Chart and Map say how
 * it is DRAWN. Nothing forces those to agree, so the set had no home for the first card
 * that crossed them — a chart of ROI over time is both, and whichever badge it got would
 * have been a lie. The failure was latent only because no such card existed yet.
 *
 * So the axis is FORM alone, and it is exhaustive over how this page can render anything:
 *
 *   Metric — one figure. Every KPI tile AND every ROI card is a Metric.
 *   Chart  — a plot over an axis.
 *   Map    — a geographic plot.
 *
 * What a metric MEANS has not gone away; it moved to where it already lived. `@repo/types`
 * `metric-taxonomy.ts` carries `MetricSolution`, `MetricRole` and `contributesToRoi` on
 * `IAnalyticsMetric`, built earlier in this epic precisely so a card can be tagged on that
 * axis. A second enum here would be the drift §44 exists to prevent. ⚠️ It is NOT wired to
 * the library today, and the reason is a real gap rather than an omission — see
 * `AnalyticsCustomize`'s note on the missing `metricId` link.
 *
 * **`table` is deliberately ABSENT.** No table card exists, and a category with no members
 * is a filter option that can only ever return nothing. Adding it costs one entry here plus
 * a re-run of the all-pairs validation on the resulting FOUR-hue set — which is not a
 * formality: the four-hue predecessor scored barely a third of what the three-hue set
 * below does (numbers under "Colour"), because a fourth hue binds a constraint three do
 * not. Add it WITH the first table card, not before it.
 *
 * ## Colour: one hue per category, and it lives HERE
 *
 * An earlier version of this comment argued for a single neutral hue on the grounds that
 * "a type carries no severity for a palette to encode". That conflates two different jobs
 * colour does. Severity is what a SEQUENTIAL or STATUS ramp encodes; a taxonomy needs
 * CATEGORICAL colour, whose job is identity, not magnitude — and identity is exactly what
 * a reader scanning a 20-row card library is trying to establish. Categories rendered in
 * one grey made the badge a second copy of the word beside it.
 *
 * The colours live in this registry rather than in the badge because the FILTER paints
 * them too. A second map in the badge file is the same drift the paragraph above exists
 * to prevent, one level down.
 *
 * ⚠️ **A hue is stored as a palette family + a step PER MODE, never as a hex, and the two
 * modes are authored independently.** The obvious version — one hue run through
 * `statusTone`, which is how every other badge in this app derives its dark mode — was
 * tried and measured, and it does not survive a multi-hue set. `statusTone`'s dark branch
 * is `lighten(hue, 0.4)`, which pushes every family to OKLCH L ≈ 0.69–0.74 and strips
 * chroma below the 0.10 floor for green and orange: validated, that derived set FAILED the
 * lightness band, the chroma floor AND CVD separation (worst all-pairs ΔE 2.5 under
 * deuteranopia — violet and blue collapsing into each other). One authored hue is enough
 * for a one-colour badge and is not enough for a palette.
 *
 * The three families were RE-DERIVED by exhaustive search after the taxonomy changed, not
 * trimmed from the four-hue set — dropping a hue changes which pair binds, so a trim
 * proves nothing. It also would have been a worse answer: the naive trim (blue/green/pink,
 * i.e. the old set minus orange) scores 33.7, against 46.0 for what ships. Scored with the
 * `dataviz` validator on ALL PAIRS — not adjacent, because any two categories can sit
 * beside each other in the library list and in the filter's trigger label. Both modes pass
 * all four computable checks:
 *
 *   light  blue[600] #2470eb · orange[600] #e9680c · pink[600] #db2982
 *          worst all-pairs ΔE 46.0 (protan)   contrast vs paper 4.57 / 3.26 / 4.51
 *   dark   blue[500] #3c83f6 · orange[600] #e9680c · pink[500] #ec4699
 *          worst all-pairs ΔE 39.6 (protan)   contrast vs paper 5.26 / 5.88 / 5.38
 *
 * Both clear the ΔE 12 target roughly threefold, so the palette does NOT lean on the 8–12
 * floor band. It has the secondary encoding anyway — every badge carries its word and its
 * own glyph — but that is not what makes it legal.
 *
 * The search's global maximum was orange/pink/purple at 58.1. This set takes 46.0 instead,
 * deliberately: it keeps the hue every surviving category ALREADY had on screen (Chart
 * orange, Map pink, and Metric inheriting KPI's blue, KPI tiles being most of what became
 * Metric), so nobody relearns a colour they just learned. 46.0 is not a compromise worth
 * agonising over at nearly 4× the target.
 *
 * Why no brand violet, which the page is otherwise built from: violet and blue are the
 * single worst pair available here (ΔE 4.3 deutan), so the brand hue can be in the set or
 * blue can, never both — and the chart's own plotted line is already `brand[500]`, so a
 * violet CHART badge would read as naming that series rather than the card's form.
 */
/**
 * ⚠️ `hue` / `step` ARE DORMANT — no consumer reads them (VERA-260).
 *
 * The long derivation above is the record of a palette that no longer renders: the badge
 * it coloured went through three narrowing passes and ended as a `text.secondary` glyph
 * and a word (see `AnalyticsCategoryLabel`'s header). Only `label` and `order` are read
 * today. The derivation is kept because it is the expensive part — if colour is ever
 * reintroduced, its reasoning and its validator runs are worth more than the hex values.
 *
 * Which is why `table`'s hue below is stated as UNVALIDATED rather than quietly added to
 * a list whose other three entries were chosen by exhaustive search. That search ran over
 * THREE hues; a fourth changes which pair binds, so its result does not extend by
 * appending — exactly the reason the note above gives for re-deriving rather than trimming
 * when the taxonomy last changed. If these are ever painted again, re-run the search over
 * all four. Green is a placeholder that keeps the record's SHAPE uniform, not a result.
 */
export const ANALYTICS_CARD_CATEGORIES = {
  metric: { label: 'Metric', order: 0, hue: 'blue', step: { light: 600, dark: 500 } },
  chart: { label: 'Chart', order: 1, hue: 'orange', step: { light: 600, dark: 600 } },
  map: { label: 'Map', order: 2, hue: 'pink', step: { light: 600, dark: 500 } },
  /**
   * VERA-260 — the first TABULAR display. A metric drawn as a ranked breakdown rather
   * than as a figure, a series or a set of points.
   *
   * Ordered LAST rather than beside Chart. The three existing values are persisted in
   * every saved view's type filter and are the order the library sorts by, so inserting
   * in the middle would renumber `map` for a cosmetic gain. Appending is the change that
   * touches nothing else.
   */
  table: { label: 'Table', order: 3, hue: 'green', step: { light: 600, dark: 500 } },
} as const

export type AnalyticsCardCategory = keyof typeof ANALYTICS_CARD_CATEGORIES

/** Every category, in authored order. The filter's option list, derived not restated. */
export const ANALYTICS_CARD_CATEGORY_ORDER = (
  Object.keys(ANALYTICS_CARD_CATEGORIES) as AnalyticsCardCategory[]
).sort((a, b) => ANALYTICS_CARD_CATEGORIES[a].order - ANALYTICS_CARD_CATEGORIES[b].order)

/** Geometry a card is created with, and the limits a resize may not cross. */
export interface AnalyticsCardSizeSpec {
  w: number
  h: number
  minW?: number
  maxW?: number
  minH?: number
  maxH?: number
  isResizable?: boolean
}

/**
 * Defaults per card kind.
 *
 * A panel's floor is 6 x 2. The WIDTH floor is measured, not guessed: 6 of 12
 * columns is ~564px inside the platform's content region at 1280, which is exactly
 * the half-width pane the superseded side-by-side layout shipped and was verified
 * at. Below it the chart's 30 daily ticks collide and the map loses its scan list.
 * The HEIGHT floor of 2 rows is 344px, enough for a plot with a readable axis.
 *
 * A card may tighten these — `ANALYTICS_PANEL_IDS.scansByLocation` does, because a
 * 400px fixed list column plus a map needs more than the generic panel does.
 *
 * ── ⚠️ THE TILE STAYS THREE COLUMNS. `w: 2` WAS BUILT, MEASURED, AND REJECTED ──────
 *
 * A narrower tile was asked for and implemented as `w/minW/maxW: 2` (six a row instead
 * of four). Measured live at a 1151px canvas (1280 viewport, sidebar collapsed) it does
 * not hold the tile's own content, so it is recorded here rather than re-attempted:
 *
 *   tile width      270px -> 172px
 *   CONTENT box     222px -> 124px   (the card carries 24px of padding a side)
 *
 * Against that 124px, with the title row costing `title + 8 gap + 16 info icon` and the
 * figure row `figure + 8 + 24 marker`:
 *
 *   TITLES     10 of 11 real titles wrap to two lines, and `Authenticity Verification
 *              Rate` (232.1px needed) wraps to THREE. At 222px only that one wraps, and
 *              it already does today.
 *   FIGURES    every currency figure truncates. `$240,586` needs 137.1px with NO marker;
 *              `$19,835.62` 162.9; `$188,900.00` 181.4. With an assumption marker even
 *              `$109.59` needs 150.6. At 222px all of them fit, worst case 213.4.
 *   HEIGHT     the killer, because it CLIPS rather than ellipsising. Each tile overflows
 *              its fixed 160px row by 36–54px, so the whole comparison line is cut off
 *              the bottom: `New` / `-16.67%` / `none in the previous period` were
 *              measured entirely below the card edge on all four Overview tiles.
 *
 * There is no middle setting to reach for: 12 columns admits 2 or 3 and nothing between,
 * and a finer grid does not help — at 20 columns a 5-across tile is 211px, whose 163px
 * content still truncates `$188,900.00`. Shrinking the figure to fit 124px would need
 * ~16px type, which is body size, so the tile would no longer have a headline.
 *
 * So `w: 2` is reachable only as a tile REDESIGN — shorter titles (§25), a smaller
 * `statSize`, and a taller row — not as a width change. §49's rule applies: size for the
 * content that must not truncate.
 *
 * ⚠️ Whenever this IS changed, it is not a default-only change. `resolveLayout`
 * deliberately re-applies `minW`/`maxW` from the CURRENT spec rather than trusting the
 * saved item, and a tile pins `minW === maxW` — so its width is owned entirely by this
 * line and every saved view clamps to it on the next load. That is the right behaviour
 * (a tile is `isResizable: false`, so nobody ever *chose* a width), but it means the
 * seeds' authored `x` coordinates must move in the same commit or the row renders with a
 * hole after every card. `analyticsViewSeeds`' logistics row is the only authored
 * geometry on the page.
 */
export const ANALYTICS_CARD_SIZE_DEFAULTS: Record<AnalyticsCardKind, AnalyticsCardSizeSpec> = {
  tile: { w: 3, h: 1, minW: 3, maxW: 3, minH: 1, maxH: 1, isResizable: false },
  panel: { w: 12, h: 4, minW: 6, minH: 2, isResizable: true },
}

/**
 * The pixel height a card of `h` rows renders at.
 *
 * `h` tiles of `ANALYTICS_ROW_HEIGHT` plus the `h - 1` gutters between them — the same
 * arithmetic react-grid-layout does internally. Exported so a height can be REASONED
 * about in rows and CHECKED in pixels, which is the only way to answer "is this card
 * taller than the viewport" without measuring a rendered DOM node.
 *
 *   h=3  568px      h=4  712px      h=5  896px      h=6  1080px      h=7  1264px
 */
export const analyticsCardPixelHeight = (h: number): number =>
  h * ANALYTICS_ROW_HEIGHT + Math.max(0, h - 1) * ANALYTICS_GRID_MARGIN[1]

/**
 * ── THE MAP CARD'S DEFAULT HEIGHT ──────────────────────────────────────────────────
 *
 * SIX rows = **1080px**. It was the generic panel default of four (712px), and the
 * difference is not a preference — it is the card's structure.
 *
 * A map card is a fixed-width RECORD LIST beside a map, so its height does two jobs at
 * once, and the list is the greedier of the two. That is already on record: `minH: 3` was
 * introduced precisely because "at two rows the list shows barely three records". Four
 * rows only moves that to about eight. At six the list shows ~13 — a screenful you can
 * actually scan against the map beside it, which is the whole reason the two are one card.
 *
 * ⚠️ **THIS IS TALLER THAN A 1280x800 VIEWPORT, and no breakpoint can fix that.**
 *
 * 1080px against an 800px-tall window means the card cannot be seen whole on a laptop.
 * The obvious repair — a shorter `h` at the narrow breakpoint — DOES NOT WORK here, and
 * the reason is worth stating so nobody spends an afternoon on it:
 * `ANALYTICS_GRID_BREAKPOINTS` measures the CANVAS CONTAINER'S WIDTH, not the viewport,
 * and the threshold is 900. At a 1280 window the canvas is ~1208px, so a 1280x800 laptop
 * is `lg` — the SAME breakpoint as a 27" monitor. The two cases this would need to
 * separate are on the same side of every breakpoint the grid has.
 *
 * The axis that would actually separate them is viewport HEIGHT, which this grid does not
 * model at all, and adding it would mean a card's stored geometry depending on the window
 * it was last looked at.
 *
 * So the cost is accepted rather than hidden, and it is bounded: the map is the LAST card
 * in every seeded view that contains it, so a laptop reader is already scrolling when they
 * reach it, and a card that then fills the screen is the normal reading position for a map.
 * `minH` stays at 3, well below the default, so anyone who disagrees can drag it to 568px
 * and their view remembers it.
 *
 * ⚠️ Existing saved views are NOT changed by this. `resolveLayout` takes `h` from the
 * saved item and only re-applies the CONSTRAINTS from the current spec, which is the
 * documented behaviour and the right one — a stored arrangement is the user's. The new
 * default reaches anyone who has not arranged this card, plus every newly added one.
 *
 * The `logistics` seed pins `h: 4` for both panels explicitly and is deliberately left
 * alone: there the map is HALF WIDTH beside the chart, read against it, and matching
 * heights is the point of that arrangement.
 */
export const ANALYTICS_MAP_CARD_SIZE: Partial<AnalyticsCardSizeSpec> = { h: 6, minH: 3 }

/** How many 3-wide tiles fit a row, per breakpoint. Derived, never hard-coded. */
export const tilesPerRow = (breakpoint: AnalyticsGridBreakpoint): number =>
  Math.max(1, Math.floor(ANALYTICS_GRID_COLS[breakpoint] / ANALYTICS_CARD_SIZE_DEFAULTS.tile.w))

/**
 * Place cards that have no authored geometry.
 *
 * Runs in two places and must give the same answer in both: when a seed authors only
 * `lg`, and when a card is added to a view that has never seen it. Tiles pack across
 * the row; anything wider than the remaining space starts a new row. Deterministic —
 * given the same id order it returns the same layout, which is what keeps a
 * regenerated `sm` from drifting between renders and flickering the active-view
 * highlight off.
 */
export const packCards = (
  cards: { id: string; size: AnalyticsCardSizeSpec }[],
  breakpoint: AnalyticsGridBreakpoint,
  startY = 0,
): AnalyticsGridItem[] => {
  const cols = ANALYTICS_GRID_COLS[breakpoint]
  const items: AnalyticsGridItem[] = []
  let x = 0
  let y = startY
  let rowHeight = 0

  for (const card of cards) {
    const w = Math.min(card.size.w, cols)
    if (x + w > cols) {
      // The next row starts below the TALLEST card in the row just closed, not below
      // the last one — otherwise a short card following a tall one would be placed
      // overlapping it, and react-grid-layout would silently push it somewhere else.
      y += rowHeight || 1
      x = 0
      rowHeight = 0
    }
    items.push({ i: card.id, x, y, w, h: card.size.h })
    x += w
    rowHeight = Math.max(rowHeight, card.size.h)
  }

  return items
}

/**
 * The layout actually handed to react-grid-layout for one breakpoint.
 *
 * Reconciles the two halves of the persisted state (§`AnalyticsViewState`): geometry
 * is taken from the saved layout where it exists, membership from `cards`. So a card
 * saved but no longer offered is dropped rather than rendered as a hole, and a card
 * added since the view was saved is placed rather than lost.
 *
 * Constraints are re-applied from the card's CURRENT spec on every call, never from
 * the saved item — that is what lets a minimum size be tightened later and take
 * effect for someone who saved a view before it changed.
 */
export const resolveLayout = (
  cards: { id: string; size: AnalyticsCardSizeSpec }[],
  saved: AnalyticsGridItem[] | undefined,
  breakpoint: AnalyticsGridBreakpoint,
) => {
  const savedById = new Map((saved ?? []).map(item => [item.i, item]))
  const cols = ANALYTICS_GRID_COLS[breakpoint]

  const placed = cards.filter(c => savedById.has(c.id))
  const unplaced = cards.filter(c => !savedById.has(c.id))

  // New cards go BELOW everything already positioned. Appending to the end is the
  // least surprising landing spot for a card the user just ticked on, and it cannot
  // displace an arrangement they built.
  const maxY = placed.reduce((acc, c) => {
    const item = savedById.get(c.id)!
    return Math.max(acc, item.y + item.h)
  }, 0)

  const generated = packCards(unplaced, breakpoint, maxY)
  const generatedById = new Map(generated.map(item => [item.i, item]))

  return cards.map(card => {
    const item = savedById.get(card.id) ?? generatedById.get(card.id)!
    const w = Math.min(Math.max(item.w, card.size.minW ?? 1), card.size.maxW ?? cols, cols)
    return {
      i: card.id,
      x: Math.min(item.x, Math.max(0, cols - w)),
      y: item.y,
      w,
      h: Math.max(item.h, card.size.minH ?? 1),
      minW: card.size.minW,
      maxW: card.size.maxW,
      minH: card.size.minH,
      maxH: card.size.maxH,
      isResizable: card.size.isResizable,
    }
  })
}

/* ────────────────────────────────────────────────────────────────────────────────────
   VARIANT CARD IDS — what a card id MEANS
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ── A CARD ID IS `<metric>[+<metric>…]::<display>`, AND THE SERIES LIST IS THE POINT ──
 *
 * These helpers live in `analyticsGrid` rather than in `analyticsDisplay`, where they
 * started, for one mechanical reason: `analyticsViewSeeds` must be able to BUILD a
 * variant id (its seeds reference chart and map cards by id), and `analyticsDisplay`
 * imports `ANALYTICS_TILE_IDS` FROM the seeds. Putting the builder there would close a
 * cycle whose failure mode is a TDZ error at module init — the seeds' top-level code
 * calling a `const` arrow that has not been evaluated yet — which surfaces as a blank
 * page, not as a compile error. This file imports nothing from either, so it is the
 * only safe home.
 *
 * ## Why the id carries a LIST of metrics, today, before anything drags onto a chart
 *
 * `VRG-467`'s composition gesture — drag a metric onto an EXISTING chart and it joins as
 * another series — changes what a chart card IS. It stops being "the chart of `scans`"
 * and becomes "a chart containing [`scans`, `certificates`]". Card ids are PERSISTED
 * (`AnalyticsViewState.cards`, plus every saved layout item's `i`, plus every user's
 * preferences row), so a scheme that assumes exactly one metric would have to be migrated
 * a second time the day that gesture lands — the same migration this change is doing now,
 * done again, against a larger installed base.
 *
 * So the grammar admits N from the start and the single-metric case is the N = 1 spelling
 * of it: `scans_count::chart` parses to `['scans_count']` and re-serialises byte-identical.
 * Nothing persisted changes today, and nothing has to change later.
 *
 * ⚠️ ORDER IS SIGNIFICANT and is NOT normalised. `a+b::chart` and `b+a::chart` are
 * different cards, because the series order decides which one owns the primary axis and
 * which colour each takes. Sorting them to make the id canonical would silently merge two
 * charts a user deliberately built differently.
 *
 * ## The separators, and why neither is `_` or `-`
 *
 * `::` between metric and display, `+` between metrics. Every existing metric id already
 * contains `_` (`scans_count`) or `-`, so either would make parsing ambiguous against an
 * id that legitimately contains it. Neither sequence appears in any id on this page.
 *
 * ⚠️ Both are PERSISTED. Changing either orphans every saved view holding a chart or map
 * card — they would be dropped as unknown ids by `intersectOrdered`, which degrades
 * quietly rather than crashing, but degrades.
 */
const VARIANT_SEPARATOR = '::'

/** Between the metrics of a multi-series card. See the note above. */
const SERIES_SEPARATOR = '+'

/**
 * ── `@<grouping>` — THE MAP'S GROUPING, CARRIED IN THE ID (VERA-260) ────────────────
 *
 * `scans_count::map` plots one mark per located SCAN; `scans_count::map@country` fills
 * one polygon per COUNTRY. Same metric, same card, two granularities — the relationship
 * the leaderboard's `Group by:` already has to its rows, and the one
 * `AnalyticsMapGrouping` declares.
 *
 * ⚠️ IT GOES IN THE ID RATHER THAN IN COMPONENT STATE, and the reason is persistence.
 * Card ids are what a saved view stores (`AnalyticsViewState.cards`, plus every layout
 * item's `i`). A grouping held in `useState` would reset to the default on every reload,
 * so a reader who saved a view "by country" would open it as pins, with nothing on screen
 * explaining why. The id grammar already encodes configuration — that is exactly what the
 * series list does — so this extends it rather than standing up a parallel per-card
 * preference store that `useAnalyticsView` would then have to persist and migrate
 * separately.
 *
 * ⚠️ `pin` IS NEVER SPELLED, and that is load-bearing for compatibility, not brevity.
 * `analyticsVariantCardId(id,'map')` and `analyticsVariantCardId(id,'map','pin')` both
 * produce `id::map` byte-identically, so every id already persisted in a saved view, a
 * seed, or a user's preferences row keeps its exact spelling and keeps resolving. Only
 * the NEW configuration gets a new id. Writing `@pin` would have orphaned every stored
 * map card the day this shipped.
 *
 * `@` because neither `_` nor `-` can be used (every metric id contains one, see above)
 * and because `::` and `+` are already taken. It appears in no id on this page.
 */
const GROUPING_SEPARATOR = '@'

/** The only grouping that is spelled. See the note above for why `pin` is not. */
const SPELLED_GROUPING = 'country' satisfies AnalyticsMapGrouping

/**
 * `scans_count` + `chart` -> `scans_count::chart`.
 * `['scans_count','certificates_count']` + `chart` -> `scans_count+certificates_count::chart`.
 * `scans_count` + `map` + `country` -> `scans_count::map@country`.
 *
 * `grouping` is only ever meaningful for `map` — the chart and the table have their own
 * axes and neither takes one. Passing it with another display is not rejected here (this
 * module knows only the GRAMMAR, per the compose/decompose note below), but nothing
 * constructs such an id and `parseAnalyticsVariantCardId` refuses to read one back, so it
 * cannot reach the canvas.
 */
export const analyticsVariantCardId = (
  metricIds: string | readonly string[],
  display: AnalyticsCardCategory,
  grouping?: AnalyticsMapGrouping,
): string =>
  `${(Array.isArray(metricIds) ? metricIds : [metricIds as string]).join(SERIES_SEPARATOR)}${VARIANT_SEPARATOR}${display}${
    grouping === SPELLED_GROUPING ? `${GROUPING_SEPARATOR}${SPELLED_GROUPING}` : ''
  }`

/**
 * The inverse. `null` for a plain (metric) card id, so callers can test membership with it.
 *
 * `metricIds` is always non-empty on a successful parse. `baseId` is its FIRST entry —
 * the primary series, and the only one a single-metric card has — kept as a named field
 * so call sites that legitimately want just the primary do not have to spell `[0]` and
 * leave a reader wondering whether the rest were dropped on purpose.
 *
 * `grouping` DEFAULTS TO `pin` for every id with no suffix, which is every id written
 * before VERA-260 and every pin-grouped id written since. So a caller reading
 * `parsed.grouping` never has to distinguish "absent" from "pins" — the two are the same
 * fact, and making it `AnalyticsMapGrouping` rather than an optional is what stops a call
 * site defaulting it a second, differently-spelled way.
 */
export const parseAnalyticsVariantCardId = (
  cardId: string,
): {
  metricIds: string[]
  baseId: string
  display: AnalyticsCardCategory
  grouping: AnalyticsMapGrouping
} | null => {
  const at = cardId.indexOf(VARIANT_SEPARATOR)
  if (at === -1) return null
  const tail = cardId.slice(at + VARIANT_SEPARATOR.length)

  /*
    Split the grouping suffix off BEFORE the category lookup, or `map@country` fails the
    `in ANALYTICS_CARD_CATEGORIES` test and the whole id parses as `null` — which is not a
    loud failure but a silent one: `intersectOrdered` would drop every country-grouped card
    out of every saved view that held one, exactly the degradation the separator note above
    warns about.
  */
  const groupingAt = tail.indexOf(GROUPING_SEPARATOR)
  const display = groupingAt === -1 ? tail : tail.slice(0, groupingAt)
  const suffix = groupingAt === -1 ? null : tail.slice(groupingAt + GROUPING_SEPARATOR.length)

  if (!(display in ANALYTICS_CARD_CATEGORIES)) return null
  /*
    A suffix that is not the one spelled grouping — or one on a display that has no
    grouping axis — is REFUSED rather than ignored. Ignoring it would resolve
    `scans_count::chart@nonsense` to the ordinary scans chart, so a corrupt or
    future-versioned id would render a card that is not the one it names.
  */
  if (suffix !== null && !(suffix === SPELLED_GROUPING && display === 'map')) return null

  const metricIds = cardId.slice(0, at).split(SERIES_SEPARATOR).filter(Boolean)
  if (metricIds.length === 0) return null
  return {
    metricIds,
    baseId: metricIds[0],
    display: display as AnalyticsCardCategory,
    grouping: suffix === SPELLED_GROUPING ? SPELLED_GROUPING : 'pin',
  }
}

/**
 * ── COMPOSE / DECOMPOSE — VRG-467's drag-a-metric-onto-a-chart gesture ──────────────
 *
 * Two inverse id transforms, both pure and both returning `null` when there is nothing
 * to do — the caller (`useAnalyticsView`) treats `null` as a no-op rather than a thrown
 * error, because both refusals here are things a drag can legitimately produce (dropping
 * a metric that is already in the card's series; removing the last series off a card
 * that only has one). A silent no-op is correct for a drop gesture: the alternative is a
 * toast for something the reader will simply try again differently, or not notice was
 * refused at all because nothing visibly changed either way.
 *
 * Neither function checks CAPABILITY (whether the dropped metric can be drawn as this
 * card's display) — that is `analyticsDisplayCapability`'s question, answered by the
 * DROP TARGET before it ever calls these, exactly as every other affordance on this page
 * states capability before the gesture rather than failing after it (see
 * `analyticsDisplay`'s header). These two only know the GRAMMAR: is `cardId` a variant,
 * and is `metricId` in or out of its list.
 */

/**
 * Append a metric to an existing variant card's series list.
 *
 * `null` when `cardId` is not a variant (a bare KPI/ROI tile — nothing to compose ONTO;
 * the target must already be a chart or a map) or when `metricId` is already one of its
 * series (refuse the duplicate — `a+a::chart` is meaningless, and re-appending it would
 * silently reorder nothing while looking like a successful drop).
 *
 * Appended to the END, never inserted or sorted — see the "ORDER IS SIGNIFICANT" note
 * above. The metric just dropped becomes the new LAST series, which is what "joins as
 * another series" means: it takes the next colour/axis slot, it does not renegotiate
 * the ones already there.
 */
export const composeAnalyticsVariantCardId = (cardId: string, metricId: string): string | null => {
  const parsed = parseAnalyticsVariantCardId(cardId)
  if (!parsed) return null
  if (parsed.metricIds.includes(metricId)) return null
  // `parsed.grouping` carried through, never re-defaulted: composing onto a card must not
  // silently regroup it. (A map refuses composition anyway — see
  // `analyticsComposeTargetRefusal` — but this function knows only the grammar, and a
  // grammar transform that drops a component of the id is a bug waiting for the day the
  // product rule changes.)
  return analyticsVariantCardId([...parsed.metricIds, metricId], parsed.display, parsed.grouping)
}

/**
 * Re-group a map card — the `Group by:` toggle's whole mechanism (VERA-260).
 *
 * A third grammar transform beside compose/decompose, and the same shape: pure, and
 * `null` when there is nothing to do (not a variant, not a map, or already at that
 * grouping). The caller — `useAnalyticsView.setCardGrouping` — treats `null` as a no-op.
 *
 * Like the other two it does NOT check capability. Whether THIS metric can be grouped
 * that way is `analyticsMapGroupingCapability`'s question, answered by the control before
 * it ever calls here, exactly as every other affordance on this page states capability
 * before the gesture rather than failing after it.
 */
export const regroupAnalyticsVariantCardId = (
  cardId: string,
  grouping: AnalyticsMapGrouping,
): string | null => {
  const parsed = parseAnalyticsVariantCardId(cardId)
  if (!parsed || parsed.display !== 'map') return null
  if (parsed.grouping === grouping) return null
  return analyticsVariantCardId(parsed.metricIds, parsed.display, grouping)
}

/**
 * Remove one metric from a composed variant's series list — the inverse gesture (a
 * per-series remove control on a composed card's legend).
 *
 * `null` in three cases, all "nothing to do": `cardId` is not a variant; `metricId` is
 * not one of its series; or removing it would leave ZERO metrics. The last case is never
 * meant to be reachable from the UI — a card always has at least one series, and taking
 * the only remaining one off is "remove the whole card", which is the pre-existing
 * per-card `X` (`onRemoveCard`), not this function. Returning `null` rather than an
 * empty-series id is what keeps that boundary a type-level fact instead of a call-site
 * convention a future caller could get wrong.
 */
export const removeAnalyticsVariantSeries = (cardId: string, metricId: string): string | null => {
  const parsed = parseAnalyticsVariantCardId(cardId)
  if (!parsed) return null
  if (!parsed.metricIds.includes(metricId)) return null
  const remaining = parsed.metricIds.filter(id => id !== metricId)
  if (remaining.length === 0) return null
  // Grouping carried through — see `composeAnalyticsVariantCardId`.
  return analyticsVariantCardId(remaining, parsed.display, parsed.grouping)
}

/**
 * ── IS `metricId` DRAWN AS `display` ANYWHERE ON THE CANVAS? (VERA-260) ────────────
 *
 * `placedIds.has(analyticsVariantCardId(metricId, display))` — an EXACT id match — is
 * what both the Layout panel's rows and the "Display as" chips used to test for "is this
 * metric shown this way already", and it is wrong the moment a chart is composed of more
 * than one metric: `certificates_count` charted only as a series inside
 * `scans_count+certificates_count::chart` does not equal `certificates_count::chart`, so
 * the exact-match test reports it as absent. The metric IS on a chart; the id just isn't
 * its own.
 *
 * This walks every canvas id, PARSES it, and asks whether `metricId` is one of its
 * series — true for a standalone single-metric card AND for a composed one, which is the
 * membership question the badge actually needs answered. It intentionally does NOT say
 * which card(s) — a metric can now legitimately sit on several charts at once (see the
 * draggability note on the two call sites), so "which one" has no single answer and this
 * function does not pretend to have one.
 */
export const isMetricDrawnAsDisplay = (
  metricId: string,
  display: AnalyticsCardCategory,
  canvasCardIds: Iterable<string>,
): boolean => {
  for (const id of canvasCardIds) {
    const parsed = parseAnalyticsVariantCardId(id)
    if (parsed && parsed.display === display && parsed.metricIds.includes(metricId)) return true
  }
  return false
}

/** Strip a react-grid-layout item back to the five fields a view persists. */
export const toPersistedItems = (
  layout: readonly { i: string; x: number; y: number; w: number; h: number }[],
): AnalyticsGridItem[] =>
  layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })).sort((a, b) => a.i.localeCompare(b.i))

/**
 * The choropleth card's id.
 *
 * A CONSTANT rather than a derived `analyticsVariantCardId`, because that card is not a
 * variant OF a metric — two of its measures (the rates) have no metric card to be a
 * variant of, which is exactly why they needed a map of their own. See
 * `analyticsDisplayVariants`' own note at its `maps.push`.
 *
 * ⚠️ IT LIVES HERE, IN THE ID-GRAMMAR MODULE, AND NOT BESIDE THE CARD (VERA-260). The card
 * is built in `pages/main/AnalyticsPage/analyticsDisplayVariants.tsx`, which renders page
 * components and therefore cannot be imported by anything under `components/analytics`
 * without inverting the dependency. `analyticsExport` needs the same id — it is the one
 * card id whose export shape cannot be inferred from the `metric::display` grammar — so
 * the choice was a second literal in a second file or one definition in the module that
 * already owns every other card id. Two literals is precisely the drift the variant note
 * warns about ("a card the library offers and that list omits is one `intersectOrdered`
 * silently drops from every saved view").
 */
/*
  ⚠️ `satisfies`, NOT an annotation (VERA-260). This card is a library ROW, so it owes a
  solution classification exactly as a KPI tile and an ROI card do — and it is the one
  library row that is NEITHER, which is how it was missed on the first pass and shipped
  briefly with no badge. `satisfies AnalyticsMetricId` asserts membership of the
  classification's key union without WIDENING this constant to that union: the id grammar
  and `analyticsExport` both rely on the literal type `'scans_by_country'`, which an
  annotation would throw away.
*/
export const SCANS_BY_COUNTRY_CARD_ID = 'scans_by_country' satisfies AnalyticsMetricId
